/** Pure helpers for the instructor "Course proposals" view. No React, no I/O. */

export const STATUS_FILTERS = [
  ["all", "All"],
  ["pending", "Under review"],
  ["approved", "Approved"],
  ["rejected", "Not approved"],
  ["cancelled", "Withdrawn"],
];

const STATUS_LABELS = {
  pending: "Under review",
  approved: "Approved",
  rejected: "Not approved",
  cancelled: "Withdrawn",
};

const REVIEW_WINDOW = "Typically reviewed in 3–5 business days";

/** Lower-cased status, defaulting to "pending" for missing values. */
export function statusOf(sub) {
  return String(sub?.status || "pending").toLowerCase();
}

export function statusLabel(status) {
  return STATUS_LABELS[status] || status;
}

/** Maps a free-text category onto a `--color-cat-*` token suffix. */
export function categoryKey(category) {
  const c = String(category || "").toLowerCase();
  if (c.includes("aha") || c.includes("cpr") || c.includes("heart")) return "aha";
  if (c.includes("ems") || c.includes("medical")) return "ems";
  if (c.includes("rescue")) return "rescue";
  if (c.includes("fire") || c.includes("officer")) return "firefighter";
  if (c.includes("police") || c.includes("law")) return "police";
  return "other";
}

export function countByStatus(subs) {
  const counts = { all: subs.length, pending: 0, approved: 0, rejected: 0, cancelled: 0 };
  subs.forEach((s) => {
    const st = statusOf(s);
    if (st in counts) counts[st] += 1;
  });
  return counts;
}

/** Filters by status and a title/ID query, then sorts by submission date ("newest" | "oldest"). */
export function filterProposals(subs, { status = "all", query = "", sort = "newest" } = {}) {
  const q = query.trim().toLowerCase();
  const time = (s) => new Date(s.submittedAt || 0).getTime();
  return subs
    .filter((s) => status === "all" || statusOf(s) === status)
    .filter((s) => !q || `${s.title || ""} ${s.id}`.toLowerCase().includes(q))
    .sort((a, b) => (sort === "oldest" ? time(a) - time(b) : time(b) - time(a)));
}

export function formatDate(iso, withYear = false) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(withYear ? { year: "numeric" } : {}) });
}

/**
 * Three-step review track: Submitted → Coordinator review → Decision.
 * Each step is { key, label, state } where state is done | now | todo | good | bad.
 */
export function reviewSteps(sub) {
  const st = statusOf(sub);
  if (st === "cancelled") return [];
  const decided = st === "approved" || st === "rejected";
  return [
    { key: "submitted", label: "Submitted", state: "done" },
    { key: "review", label: decided ? "Coordinator review" : "With coordinator", state: decided ? "done" : "now" },
    {
      key: "decision",
      label: st === "approved" ? "Approved" : st === "rejected" ? "Not approved" : "Decision",
      state: st === "approved" ? "good" : st === "rejected" ? "bad" : "todo",
    },
  ];
}

/** Right-hand caption under the status tag. */
export function statusCaption(sub) {
  const st = statusOf(sub);
  if (st === "pending") return REVIEW_WINDOW;
  if (st === "cancelled") return "Withdrawn";
  return sub.reviewedAt ? `${formatDate(sub.reviewedAt)} · Coordinator` : "";
}

export function reviewNote(sub) {
  return sub.reviewNotes || (statusOf(sub) === "pending" ? REVIEW_WINDOW : "");
}

export function splitPrerequisites(value) {
  return value ? String(value).split(/[,;]/).map((p) => p.trim()).filter(Boolean) : [];
}
